import React, {useEffect, useRef} from 'react';
import {ErrorMessage, Flex, Input, Text} from '@priority-software/priority-style-react';

import cx from 'classnames';
import text from './texts';
import {useEventListener} from '../useEventListener';
import {keys} from '../../constants/KeyCodes';
import styles from './styles.module.scss';
import {isRTLString} from '@priority-software/priority-utils/lib/strings';
import {isRTL} from '../../Localization';

type Props = {
    isCopyAction: boolean;
    keyId: string;
    handleChangeId: (e) => void;
    value: string;
    handleDescription: (e) => void;
    description: string;
    handleSearch: (e) => void;
    search: string;
    message: string;
    isOpenDeleteDialog: boolean;
};

export const Content = ({
    isCopyAction,
    keyId,
    value,
    description,
    search,
    message,
    isOpenDeleteDialog,
    handleChangeId,
    handleDescription,
    handleSearch
}: Props) => {
    const keyInputRef = useRef<HTMLInputElement>(null);
    const descInputRef = useRef<HTMLInputElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        requestAnimationFrame(() => {
            isCopyAction
                ? (keyInputRef.current as HTMLInputElement)?.select()
                : (searchInputRef.current as HTMLInputElement)?.focus();
        });
    }, []);

    const handlerKeyboard = (e: KeyboardEvent) => {
        e.stopPropagation();
        if (!isOpenDeleteDialog) {
            const keyCode = e.keyCode;
            const allowTextKey =
                (keyCode >= 48 && keyCode <= 57) || (keyCode >= 65 && keyCode <= 90);

            if (allowTextKey && !isCopyAction) {
                if (searchInputRef?.current !== document.activeElement) {
                    searchInputRef?.current?.focus();
                }
            }
            if (isCopyAction) {
                switch (e.key) {
                    case keys.ARROW_LEFT: {
                        isRTL() ? descInputRef.current?.focus() : keyInputRef.current?.focus();
                        break;
                    }
                    case keys.ARROW_RIGHT: {
                        isRTL() ? keyInputRef.current?.focus() : descInputRef.current?.focus();
                        break;
                    }
                    default:
                        break;
                }
            }
        }
    };

    useEventListener('keydown', handlerKeyboard);

    return (
        <div ref={contentRef}>
            {isCopyAction ? (
                <Flex componentClasses={styles.headerInputs}>
                    <div className={styles.inputContainer}>
                        <Text skin="paragraph-1" textColor="primary-700">
                            {text.no_field}
                            <span className={styles.requiredMark}>*</span>
                        </Text>
                        <Input
                            inputRef={keyInputRef}
                            autoFocus
                            value={keyId}
                            maxLength={2}
                            type="number"
                            onChange={handleChangeId}
                            componentClasses={cx(styles.input, styles.idInput)}
                            size="large"
                        />
                    </div>
                    <div className={styles.inputContainer}>
                        <Text skin="paragraph-1" textColor="primary-700">
                            {text.value_field}
                        </Text>
                        <Input
                            disabled
                            value={value || ''}
                            componentClasses={cx(
                                styles.input,
                                styles.valueInput,
                                isRTLString(value) ? styles.forceRtl : styles.forceLtr
                            )}
                            size="large"
                        />
                    </div>
                    <div className={styles.inputContainer}>
                        <Text skin="paragraph-1" textColor="primary-700">
                            {text.description_Field}
                        </Text>
                        <Input
                            inputRef={descInputRef}
                            onChange={handleDescription}
                            value={description}
                            componentClasses={cx(styles.input, styles.decsInput)}
                            size="large"
                        />
                    </div>
                </Flex>
            ) : (
                <div className={styles.inputContainer}>
                    <p>{text.search_by_num_or_description}</p>
                    <Input
                        inputRef={searchInputRef}
                        componentClasses={styles.pasteInput}
                        autoFocus
                        value={search}
                        onChange={handleSearch}
                        size="large"
                    />
                </div>
            )}
            {message && <ErrorMessage message={message} componentClasses={styles.errorMsg} />}
        </div>
    );
};
